import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { sendSupportEmail } from "../../api";
import { SendEmailRequest } from "../../api/models";


interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const EmailDialog = ({ open, setOpen }: Props) => {
    const cancelButtonRef = useRef(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [question, setQuestion] = useState<string>("");
    const [invalid, setInvalid] = useState<boolean>(false);
    const [sent, setSent] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(false);

    useEffect(() => {
        if (!sent) {
            setDisabled(false);
        }
    }, [sent]);

    const sendQuestion = async () => {
        if (!question.trim()) {
            setInvalid(true);
            return;
        }

        setDisabled(true);

        try {
            const emailRequest: SendEmailRequest = {
                subject: 'ChatJTC Feedback',
                question: question
            };
            await sendSupportEmail(emailRequest).then(res => {
                if (res) {
                    setSent(true);
                    setInvalid(false);
                    setQuestion("");
                    setTimeout(() => {
                        setOpen(false);
                        setSent(false);
                    }, 2000);
                } else {
                    setDisabled(false);
                }
            });
        } catch (error) {
            console.error('Error sending email:', error);
            setDisabled(false);
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!ev.target.value) {
            setQuestion("");
        } else {
            setInvalid(false);
            setQuestion(ev.target.value);
        }
    };

    const onClose = () => {
        setOpen(false);
        setInvalid(false);
        setQuestion("");
        setSent(false);
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog className="relative z-[100]" initialFocus={cancelButtonRef} onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-jtc-very-dark-grey bg-opacity-50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg rounded-md">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-3 sm:ml-4 sm:mt-0 text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Send Feedback Email
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    If ChatJTC was unable to answer your question or you have a suggestion, please feel free to reach out to us below.
                                                </p>
                                            </div>
                                            {!sent ? (
                                                <textarea
                                                    ref={textAreaRef}
                                                    className={
                                                        "text-sm mt-3 mr-2 w-full resize-none bg-transparent focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 p-2 h-[200px] border placeholder-jtc-very-dark-grey" +
                                                        (invalid
                                                            ? " border-jtc-pink focus:border-jtc-pink focus-visible:border-jtc-pink"
                                                            : " border-jtc-grey focus:border-jtc-grey focus-visible:border-jtc-grey")
                                                    }
                                                    placeholder={invalid ? "A message is required..." : "Enter your message here..."}
                                                    rows={1}
                                                    value={question}
                                                    onChange={onQuestionChange}
                                                ></textarea>
                                            ) : (
                                                <div className={"text-sm mt-3 mr-2 w-full bg-transparent p-2 h-[200px] placeholder-jtc-very-dark-grey"}>
                                                    <div className="flex flex-col items-center justify-center h-full">
                                                        <div className="">
                                                            <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                                                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"></circle>
                                                                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"></path>
                                                            </svg>
                                                        </div>
                                                        <div className="">Message sent!</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="px-4 pb-6 flex flex-row sm:flex-row-reverse sm:px-6 gap-4">
                                    <button className="jtc-secondary-button m-0 w-[115px]" onClick={onClose}>
                                        <span>
                                            <span>Close</span>
                                        </span>
                                    </button>
                                    <button
                                        className="jtc-primary-button m-0 w-[115px] disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={sendQuestion}
                                        disabled={disabled}
                                    >
                                        <span>
                                            <span>Send</span>
                                        </span>
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};